// update-check.ts — make an ALREADY-RUNNING client notice a new build (#206).
// The cold-load stale-build problem is solved by sw.js (network-first
// navigations + build-hash-keyed caches), but a client that never re-navigates
// — an iOS Home-Screen PWA resumed from the app switcher, or a long-lived
// browser tab across a server upgrade — used to keep executing the old bundle
// forever. This is the logic half: main.ts feeds it the browser events
// (visibilitychange/pageshow, a periodic timer, the service worker's
// controllerchange) and it decides WHEN to poll for a new sw.js and WHEN a
// staged update may safely reload the page. Dependency-injected so it
// node-tests without a browser (test/client/update-check.test.ts).

export interface UpdateCheckDeps {
  /** poll for a newer build — navigator.serviceWorker.getRegistration()?.update().
   *  A byte-different sw.js installs → skipWaiting/clients.claim (sw.js) →
   *  controllerchange fires on the page → main.ts calls updateActivated(). */
  checkForUpdate(): Promise<void>;
  /** true when reloading now loses nothing — no running game, no MP flow. */
  safeToReload(): boolean;
  /** perform the reload. Navigations are network-first (sw.js), so a plain
   *  location.reload() lands on the fresh build. */
  reload(): void;
  now(): number;
  /** min gap between checkForUpdate() polls — resume events can burst (iOS
   *  fires visibilitychange AND pageshow on one app-switch). Default 60s. */
  minCheckGapMs?: number;
}

const kMinCheckGapMs = 60_000;

export class UpdateCheck {
  private readonly d: UpdateCheckDeps;
  private lastCheck = -Infinity;
  private updateReady = false;
  private reloaded = false;

  constructor(d: UpdateCheckDeps) {
    this.d = d;
  }

  /** A resume signal (visibilitychange→visible, pageshow, the long-lived-tab
   *  periodic timer): land any already-staged update first, then poll the SW
   *  for a newer build (throttled; an offline poll failing is fine). */
  resume(): void {
    this.maybeReload();
    if (this.reloaded) return;
    const t = this.d.now();
    if (t - this.lastCheck < (this.d.minCheckGapMs ?? kMinCheckGapMs)) return;
    this.lastCheck = t;
    this.d.checkForUpdate().catch(() => {
      /* offline/transient — next resume retries */
    });
  }

  /** A NEW build's service worker took control (controllerchange with a prior
   *  controller — main.ts filters out the first-install clients.claim()). The
   *  new assets are precached and active; reload now if safe, else stay armed
   *  until a maybeReload() finds a safe moment (e.g. the running game ends). */
  updateActivated(): void {
    this.updateReady = true;
    this.maybeReload();
  }

  /** an update is staged and waiting for a safe moment to reload. */
  pending(): boolean {
    return this.updateReady && !this.reloaded;
  }

  /** reload once, the first time an update is pending AND the moment is safe. */
  maybeReload(): void {
    if (!this.updateReady || this.reloaded) return;
    if (!this.d.safeToReload()) return;
    this.reloaded = true;
    this.d.reload();
  }
}
